import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import {
  getStoredAccessToken,
  decodeJwtPayload,
  getPermissionsFromToken,
  getTokenBasedUserType,
} from "@/utils/keycloakRoles";
import { keycloakApi } from "@/integrations/keycloak/client";

export interface UserProfile {
  user_id: string;
  user_name: string | null;
  email: string;
  phone_number: string | null;
  permission_role: string;
  is_internal: boolean;
  /** ID do generator na nova engine (Keycloak) - corresponde a id_new_engine_generators */
  generator_id: number | null;
  roles: string[];
}

export const useProfile = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    data: profile,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["user-profile-keycloak"],
    queryFn: async () => {
      // Obter token e decodificar payload
      const token = getStoredAccessToken();
      if (!token) {
        throw new Error("Token não encontrado");
      }

      const payload = decodeJwtPayload(token);
      if (!payload) {
        throw new Error("Token inválido");
      }

      // Tipo de usuário: regra única (iss + role) — única fonte para is_internal e permission_role
      const userType = getTokenBasedUserType();
      if (!userType)
        throw new Error("Não foi possível determinar o tipo de usuário");

      const email = payload.email || "";
      const fullname = payload.name || payload.preferred_username || "";

      // Buscar dados completos do usuário da API Keycloak usando o email
      // Apenas para usuários do realm generator — usuários do realm musa (internos)
      // não têm registros nessa API e não devem buscar dados dela para evitar
      // mistura com usuários generator que tenham o mesmo email.
      if (!userType.isInternal) try {
        const response = await keycloakApi.fetchUsers({ emailFilter: email });
        const currentUser = response.results?.find((u) => u.email === email);

        if (currentUser) {
          return {
            user_id: currentUser.id.toString(),
            user_name: currentUser.fullname,
            email: currentUser.email,
            phone_number: currentUser.phone || null,
            permission_role: userType.permission_role,
            is_internal: userType.isInternal,
            generator_id: currentUser.generator_id ?? null,
            roles: getPermissionsFromToken(),
          } as UserProfile;
        }
      } catch (err) {
        // Se falhar ao buscar da API, usar dados do token
      }

      // Fallback: usar apenas dados do token (mesmo userType da regra única)
      let generatorId: number | null = null;
      if (payload.generator_id) {
        const parsed =
          typeof payload.generator_id === "number"
            ? payload.generator_id
            : parseInt(payload.generator_id as string, 10);
        if (!isNaN(parsed)) generatorId = parsed;
      } else if (payload.generator) {
        const parsed =
          typeof payload.generator === "number"
            ? payload.generator
            : parseInt(payload.generator as string, 10);
        if (!isNaN(parsed)) generatorId = parsed;
      } else if (payload.generators?.[0]?.id) {
        // Fallback: derivar do primeiro vínculo em generators[] quando claims legados estiverem ausentes
        const parsed = parseInt(payload.generators[0].id, 10);
        if (!isNaN(parsed)) generatorId = parsed;
      }

      return {
        user_id: payload.sub || "",
        user_name: fullname,
        email,
        phone_number: null,
        permission_role: userType.permission_role,
        is_internal: userType.isInternal,
        generator_id: generatorId,
        roles: getPermissionsFromToken(),
      } as UserProfile;
    },
    enabled: true,
    retry: 1,
  });

  const updateProfileMutation = useMutation({
    mutationFn: async ({
      user_name,
      phone_number,
    }: {
      user_name: string;
      phone_number: string | null;
    }) => {
      if (!profile?.user_id) throw new Error("Usuário não autenticado");

      // Atualizar via API Keycloak
      const userId = parseInt(profile.user_id, 10);
      if (isNaN(userId)) {
        throw new Error("ID do usuário inválido");
      }

      await keycloakApi.updateUser(userId, {
        fullname: user_name.trim(),
        phone: phone_number?.trim() || null,
      });

      return { user_name, phone_number };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-profile-keycloak"] });
      queryClient.invalidateQueries({ queryKey: ["users-with-keycloak"] });
      toast({
        title: "Perfil atualizado",
        description: "Suas informações foram atualizadas com sucesso.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao atualizar perfil",
        description:
          error.message || "Ocorreu um erro ao atualizar suas informações.",
        variant: "destructive",
      });
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: async ({ newPassword }: { newPassword: string }) => {
      // Nota: Alterar senha no Keycloak requer endpoint específico
      // Por enquanto, vamos mostrar mensagem que isso deve ser feito via Keycloak
      throw new Error(
        "A alteração de senha deve ser feita através do sistema Keycloak. Entre em contato com o administrador.",
      );
    },
    onSuccess: () => {
      toast({
        title: "Senha alterada",
        description: "Sua senha foi alterada com sucesso.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao alterar senha",
        description: error.message || "Ocorreu um erro ao alterar sua senha.",
        variant: "destructive",
      });
    },
  });

  return {
    profile,
    isLoading,
    error,
    updateProfile: updateProfileMutation.mutate,
    changePassword: changePasswordMutation.mutate,
    isUpdatingProfile: updateProfileMutation.isPending,
    isChangingPassword: changePasswordMutation.isPending,
  };
};
